import {
  Bell,
  Check,
  Clipboard,
  ChevronRight,
  Search,
} from 'lucide-react'
import { useMemo, useState, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { DashboardLayout } from '../../components/layout/DashboardLayout'
import { OpenIndicator } from '../../components/ui/OpenIndicator'
import { Breadcrumb } from '../../components/ui/Breadcrumb'
import { shellAssets, notificationAssets } from '../../constants/assets'
import { dashboardRoutes } from '../../constants/routes'
// import { useDashboardBadges } from '../../context/DashboardBadgeContext'
import {
  apiGetNotifications,
  apiMarkNotificationAsRead,
  type NotificationAction,
  type NotificationGroup,
  type NotificationItem,
} from '../../api/notifications/notificationsApi'
import { cn } from '../../utils/cn'

const currentUser = {
  name: 'Admin',
  email: 'Admin@123gmail.com',
  avatar: shellAssets.avatar,
}

const categories = [
  'All',
  'Bookings',
  'Customers',
  'Drivers',
  'Payments',
  'Reviews',
  'Reports',
  'System',
] as const

type NotificationFilter = (typeof categories)[number]

const groups: NotificationGroup[] = [
  'Today–22 May 2026',
  'Yesterday',
]

const quickActions: Array<{
  label: string
  icon: string
  path?: string
}> = [
  {
    label: 'View All Bookings',
    icon: notificationAssets.sideBox1,
    path: dashboardRoutes.bookings,
  },
  {
    label: 'Assign Drivers',
    icon: notificationAssets.sideBox5,
    path: dashboardRoutes.drivers,
  },
  {
    label: 'Manage Fleet',
    icon: notificationAssets.sideBox6,
    path: dashboardRoutes.services,
  },
  {
    label: 'Open Calendar',
    icon: notificationAssets.sideBox7,
    path: dashboardRoutes.bookings,
  },
  {
    label: 'View Payments',
    icon: notificationAssets.sideBox8,
    path: dashboardRoutes.payments,
  },
  {
    label: 'Download Reports',
    icon: notificationAssets.sideBox9,
  },
]

const NotificationsPage = () => {
  const navigate = useNavigate()
  // const { markNotificationsAsSeen } = useDashboardBadges()
  const [, setLoading] = useState(false)

  const [notificationItems, setNotificationItems] = useState<NotificationItem[]>([])

  // Load from API and mark notifications as seen
  useEffect(() => {
    let mounted = true
    const initNotifications = async () => {
      setLoading(true)

      try {
        const res = await apiGetNotifications()
        if (mounted) {
          if (res.success && Array.isArray(res.data)) {
            setNotificationItems(res.data)
          } else {
            setNotificationItems([])
          }
        }
      } catch (err) {
        console.error('Failed to fetch notifications', err)
        if (mounted) {
          setNotificationItems([])
        }
      } finally {
        if (mounted) setLoading(false)
      }

      // Mark seen on server and locally
      // apiMarkAllNotificationsAsRead().catch(() => null)
      // markNotificationsAsSeen()
      // if (mounted) {
      //   setNotificationItems((prev) =>
      //     prev.map((item) => (item.isNew ? { ...item, isNew: false } : item))
      //   )
      // }
    }

    initNotifications()
    return () => {
      mounted = false
    }
  }, [])

  const overviewItems = useMemo(() => {
    const totalToday = notificationItems.filter((item) =>
      item.group.toLowerCase().startsWith('today')
    ).length
    const unreadToday = notificationItems.filter(
      (item) => !item.is_read && item.group.toLowerCase().startsWith('today')
    ).length

    return [
      {
        label: 'Total Today',
        value: totalToday,
        icon: notificationAssets.sideBox1,
      },
      {
        label: 'Unread Today',
        value: unreadToday,
        icon: notificationAssets.sideBox2,
      },
      {
        label: 'Action Required',
        value: notificationItems.filter((item) => item.actions && item.actions.length > 0).length,
        icon: notificationAssets.sideBox3,
      },
      {
        label: 'System Alerts',
        value: notificationItems.filter((item) => item.category === 'System').length,
        icon: notificationAssets.sideBox4,
      },
    ]
  }, [notificationItems])

  const [query, setQuery] = useState('')
  const [activeFilter, setActiveFilter] =
    useState<NotificationFilter>('All')
  const [filtersOpen, setFiltersOpen] = useState(false)

  const groupedNotifications = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    const filtered = notificationItems.filter((notification) => {
      const matchesCategory =
        activeFilter === 'All' ||
        notification.category === activeFilter

      const matchesQuery =
        normalizedQuery.length === 0 ||
        notification.title
          .toLowerCase()
          .includes(normalizedQuery) ||
        notification.description
          .toLowerCase()
          .includes(normalizedQuery)

      return matchesCategory && matchesQuery
    })

    // const allGroups = Array.from(
    //   new Set([...groups, ...filtered.map((n) => n.group)])
    // )
    const allGroups = Array.from(
      new Set([...groups, ...filtered.map((n) => n.group)])
    ).sort((a, b) => {
      const getDate = (group: string) => {
        if (group.startsWith('Today')) return new Date().setHours(0, 0, 0, 0)
        if (group === 'Yesterday') {
          const yesterday = new Date()
          yesterday.setDate(yesterday.getDate() - 1)
          return yesterday.setHours(0, 0, 0, 0)
        }

        return new Date(group).getTime()
      }

      return getDate(b) - getDate(a)
    })
    return allGroups
      .map((group) => ({
        group,
        items: filtered.filter(
          (notification) =>
            notification.group === group,
        ),
      }))
      .filter(({ items }) => items.length > 0)
  }, [activeFilter, notificationItems, query])

  const handleAction = (action: NotificationAction) => {
    if (action.path) {
      navigate(action.path)
    }
  }

  
  const handleOpenNotification = async (id: number | string) => {
    const notification = notificationItems.find((item) => item.id === id)

    if (!notification || notification.is_read) return

    const result = await apiMarkNotificationAsRead(id)

    if (result.success) {
      setNotificationItems((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, is_read: true, isNew: false }
            : item
        )
      )
    }
  }


  return (
    <DashboardLayout
      activeNavId="notifications"
      subtitle={
        <Breadcrumb
          items={[
            {
              label: 'Dashboard',
              path: dashboardRoutes.overview,
            },
            {
              label: 'Bookings',
              path: dashboardRoutes.bookings,
            },
            {
              label: 'New Booking',
            },
          ]}
        />
      }
      title="Notifications"
      user={currentUser}
    >
      {/*
        ONLY CHANGE:
        This Notifications page content scrolls inside the
        available area below the top navigation.

        Therefore the content will no longer move behind
        the sticky top navigation.
      */}
    <div
      className="
        grid
        gap-[20px]
        pb-[30px]
        pr-[6px]
        xl:grid-cols-[minmax(0,1fr)_294px]
        xl:items-start
      "
    >
        <main className="min-w-0">
          {/* ================= SUMMARY CARDS ================= */}

      <div className="sticky top-0 z-30 bg-dash-bg pb-[18px]">
        <section
          aria-label="Notification summary"
          className="grid grid-cols-1 gap-[18px] sm:grid-cols-3 xl:gap-[30px]"
        >
          <NotificationStat
            icon={notificationAssets.total}
            label="Total Notifications"
            value={notificationItems.length.toString()}
          />

          <NotificationStat
            icon={notificationAssets.unread}
            label="Unread"
            value={notificationItems.filter((item) => !item.is_read).length.toString()}
          />

          <NotificationStat
            icon={notificationAssets.today}
            label="Today"
            value={notificationItems
              .filter((item) => item.group.toLowerCase().startsWith('today'))
              .length.toString()}
          />
        </section>
      </div>

{/* ================= FILTERS ================= */}

          {/* ================= FILTERS ================= */}

         <section aria-label="Notification filters">
            <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center">
              <label className="flex h-[56px] w-full max-w-[675px] items-center gap-[12px] rounded-[12px] border border-[#e2e2e2] bg-white px-[17px] shadow-dash-card">
                <Search
                  aria-hidden
                  className="size-[20px] shrink-0 stroke-[1.8] text-black"
                />

                <input
                  aria-label="Search notifications"
                  className="min-w-0 flex-1 bg-transparent font-host text-[14px] font-medium text-black outline-none placeholder:text-black"
                  onChange={(event) =>
                    setQuery(event.target.value)
                  }
                  placeholder="Search"
                  type="search"
                  value={query}
                />
              </label>

              <div className="relative">
                <button
                  aria-expanded={filtersOpen}
                  className="flex h-[45px] items-center gap-[8px] rounded-[10px] border border-[#b7b7b7] bg-white px-[16px] font-host text-[13px] font-medium text-black transition hover:bg-[#fafafa]"
                  onClick={() =>
                    setFiltersOpen((open) => !open)
                  }
                  type="button"
                >
                  <img
                    alt=""
                    aria-hidden
                    className="block h-[12px] w-[13px] max-w-none object-contain"
                    src={notificationAssets.filter}
                  />

                  More Filters
                </button>

                {filtersOpen ? (
                  <div className="absolute left-0 top-[52px] z-20 w-[190px] rounded-[12px] border border-[#e6e6e6] bg-white p-[10px] shadow-dash-panel">
                    <button
                      className="flex w-full items-center justify-between rounded-[8px] px-[10px] py-[9px] text-left font-host text-[13px] text-black hover:bg-[#f5f5f5]"
                      onClick={() =>
                        setActiveFilter('All')
                      }
                      type="button"
                    >
                      Show all notifications

                      {activeFilter === 'All' ? (
                        <Check className="size-[15px]" />
                      ) : null}
                    </button>

                    <button
                      className="mt-[2px] flex w-full items-center justify-between rounded-[8px] px-[10px] py-[9px] text-left font-host text-[13px] text-black hover:bg-[#f5f5f5]"
                      onClick={() =>
                        setActiveFilter('Bookings')
                      }
                      type="button"
                    >
                      Booking alerts

                      {activeFilter === 'Bookings' ? (
                        <Check className="size-[15px]" />
                      ) : null}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            <div
              className="mt-[17px] flex flex-wrap items-center gap-[10px]"
              role="tablist"
            >
              {categories.map((category) => {
                const active =
                  activeFilter === category

                return (
                  <button
                    aria-selected={active}
                    className={cn(
                      'h-[37px] rounded-[7px] bg-white px-[14px] font-host text-[13px] font-medium text-black transition hover:bg-[#f7f7f7]',
                      active &&
                        'border border-[#4E73E4] text-[#365E92]',
                    )}
                    key={category}
                    onClick={() =>
                      setActiveFilter(category)
                    }
                    role="tab"
                    type="button"
                  >
                    {category}
                  </button>
                )
              })}
            </div>
          </section>

          {/* ================= NOTIFICATIONS LIST ================= */}

          <section
            aria-label="Notifications list"
            className="mt-[25px]"
          >
            {groupedNotifications.length > 0 ? (
              <div className="space-y-[26px]">
                {groupedNotifications.map(
                  ({ group, items }) => (
                    <section key={group}>
                      <h2 className="font-host text-[15px] font-semibold text-black">
                        {group}
                      </h2>

                      <div className="mt-[21px] space-y-[24px]">
                        {items.map(
                          (notification) => (
                            <NotificationRow
                                key={notification.id}
                                notification={notification}
                                onAction={handleAction}
                                onOpen={handleOpenNotification}
                            />
                          ),
                        )}
                      </div>
                    </section>
                  ),
                )}
              </div>
            ) : (
              <div className="flex min-h-[220px] flex-col items-center justify-center rounded-[16px] bg-white px-6 text-center shadow-dash-card">
                <Bell className="size-8 text-[#6c6c6c]" />

                <p className="mt-3 font-host text-[15px] font-semibold text-black">
                  No matching notifications
                </p>

                <p className="mt-1 font-host text-[13px] text-dash-muted">
                  Try another search or category.
                </p>
              </div>
            )}
          </section>
        </main>

        {/* ================= RIGHT SIDE ================= */}

        <aside className="flex flex-col gap-[18px] xl:pt-[257px]">
          <DashboardSideCard title="Today’s Overview">
            <div className="mt-[15px] space-y-[15px]">
              {overviewItems.map((item) => (
                <OverviewRow
                  key={item.label}
                  {...item}
                />
              ))}
            </div>
          </DashboardSideCard>

          <DashboardSideCard title="Quick Actions">
            <div className="mt-[14px] space-y-[13px]">
              {quickActions.map((action) => (
                <button
                  className="flex w-full items-center justify-between gap-3 text-left"
                  key={action.label}
                  onClick={() =>
                    action.path &&
                    navigate(action.path)
                  }
                  type="button"
                >
                  <span className="flex min-w-0 items-center gap-[10px]">
                    <img
                      alt=""
                      aria-hidden
                      className="block size-[37px] max-w-none shrink-0 object-contain"
                      src={action.icon}
                    />

                    <span className="truncate font-host text-[13px] font-medium text-black">
                      {action.label}
                    </span>
                  </span>

                  <ChevronRight
                    aria-hidden
                    className="size-[18px] shrink-0 stroke-[1.8] text-black"
                  />
                </button>
              ))}
            </div>
          </DashboardSideCard>
        </aside>
      </div>
    </DashboardLayout>
  )
}

type NotificationStatProps = {
  icon: string
  label: string
  value: string
}

const NotificationStat = ({
  icon,
  label,
  value,
}: NotificationStatProps) => (
  <article className="flex h-[100px] items-center justify-between gap-4 rounded-[12px] bg-dash-card px-[22px] shadow-dash-card">
    <div className="flex min-w-0 items-center gap-[12px]">
      <img
        alt=""
        aria-hidden
        className="block size-[35px] max-w-none shrink-0 object-contain"
        src={icon}
      />

      <div className="min-w-0">
        <p className="font-host text-[20px] font-medium leading-none text-black">
          {value}
        </p>

        <p className="mt-[7px] whitespace-nowrap font-host text-[13px] font-medium leading-none text-[#39a02e]">
          {label}
        </p>
      </div>
    </div>

    <OpenIndicator />
  </article>
)

type NotificationRowProps = {
  notification: NotificationItem
  onAction: (action: NotificationAction) => void
  onOpen: (id: number | string) => void
}


const NotificationRow = ({
  notification,
  onAction,
  onOpen,
}: NotificationRowProps) => (
  <article className="grid min-h-[54px] grid-cols-[45px_minmax(0,1fr)] items-center gap-x-[16px] gap-y-[12px] md:grid-cols-[45px_minmax(0,1fr)_86px_270px]">
    <span className="flex size-[45px] shrink-0 items-center justify-center rounded-full border border-[#dedede] bg-white">
      <img
        alt=""
        aria-hidden
        className={cn(
          'block max-w-none object-contain',
          notification.iconSize,
        )}
        src={notification.icon}
      />
    </span>

    <button
      type="button"
      onClick={() => onOpen(notification.id)}
      className="min-w-0 self-center text-left"
    >
      <div className="flex flex-wrap items-center gap-[7px]">
        <h3 className="font-host text-[14px] font-semibold leading-[1.25] text-black">
          {notification.title}
        </h3>

        {!notification.is_read ? (
          <span className="rounded-[5px] bg-[#66b64b] px-[7px] py-[2px] font-host text-[10px] font-semibold leading-none text-white">
            New
          </span>
        ) : null}
      </div>

      <p className="mt-[5px] max-w-[490px] font-host text-[13px] font-normal leading-[1.35] text-[#797979]">
        {notification.description}
      </p>
      {notification.booking_reference ? (
        <div className="mt-[4px] flex flex-wrap items-center gap-[8px]">
          <p className="font-host text-[12px] font-medium text-[#4E73E4]">
            Booking ID: {notification.booking_reference}
          </p>

          <button
            type="button"
            onClick={() =>
              navigator.clipboard.writeText(notification.booking_reference!)
            }
            className="inline-flex items-center gap-[4px] rounded-[5px] border border-[#d9d9d9] px-[7px] py-[3px] font-host text-[11px] font-medium text-[#555] transition hover:bg-[#f5f5f5]"
            title="Copy booking ID"
            aria-label={`Copy booking ID ${notification.booking_reference}`}
          >
            <Clipboard className="size-[12px]" />
            Copy
          </button>
        </div>
      ) : null}
    </button>

    <time className="col-start-2 whitespace-nowrap font-host text-[13px] font-semibold text-black md:col-start-auto md:text-center">
      {notification.time}
    </time>

    <div className="col-span-2 flex flex-wrap items-center gap-[10px] md:col-span-1 md:flex-nowrap md:justify-end">
      {notification.actions.map((action) => (
        <button
          className={cn(
            'h-[42px] whitespace-nowrap rounded-full border px-[16px] font-host text-[13px] font-medium transition active:scale-[0.98]',
            action.variant === 'danger' &&
              'border-[#ef3f0d] bg-[#ef3f0d] text-white hover:bg-[#d9360b]',
            action.variant === 'success' &&
              'border-[#1f650e] bg-[#1f650e] text-white hover:bg-[#185408]',
            (!action.variant || action.variant === 'primary') &&
              'border-[#4e73e4] bg-white text-[#3569f0] hover:bg-[#f6f8ff]',
          )}
          key={action.label}
          onClick={() => onAction(action)}
          type="button"
        >
          {action.label}
        </button>
      ))}
    </div>
  </article>
)


type DashboardSideCardProps = {
  title: string
  children: ReactNode
}

const DashboardSideCard = ({
  title,
  children,
}: DashboardSideCardProps) => (
  <section className="rounded-[18px] bg-white px-[20px] py-[20px] shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
    <h2 className="font-host text-[15px] font-semibold text-black">
      {title}
    </h2>

    {children}
  </section>
)

type OverviewRowProps = {
  label: string
  value: number
  icon: string
}

const OverviewRow = ({
  label,
  value,
  icon,
}: OverviewRowProps) => (
  <div className="flex items-center justify-between gap-3">
    <span className="flex min-w-0 items-center gap-[10px]">
      <img
        alt=""
        aria-hidden
        className="block size-[37px] max-w-none shrink-0 object-contain"
        src={icon}
      />

      <span className="truncate font-host text-[13px] font-medium text-black">
        {label}
      </span>
    </span>

    <span className="flex size-[24px] shrink-0 items-center justify-center rounded-full bg-[#f3f3f3] font-host text-[11px] font-medium text-black">
      {value}
    </span>
  </div>
)

export default NotificationsPage